import queryString from 'query-string'
import { has, isNil, isString } from 'lodash'

import { apiErrorMessage, apiService, fetchingAPI } from '@/api'

// Every thunk resolves to { data } or { error: { kind, message } } and never rejects
// or toasts: the Manage files modal renders each failure inline.
export const NOT_SYNCED = 'notSynced'
export const REMOVAL_INCOMPLETE = 'removalIncomplete'
export const REQUEST_FAILED = 'requestFailed'
export const INVALID_CURSOR = 'invalidCursor'
export const DRIVE_NOT_FOUND = 'driveNotFound'
export const NOT_AWAITING_REVIEW = 'notAwaitingReview'
export const IN_REVIEW = 'inReview'
export const NOT_FOUND = 'notFound'

const BASE = () => `${apiService.agentGpt}/document_source_connections`
const USE_OWN_ORG = true
const SILENT = { isSilent: true }
const NOT_FOUND_STATUS = 404
const GATEWAY_TIMEOUT = 504
// A save commits before files are removed, and removal runs on after the ingress cuts the
// request off, so a 504 from a save or a finish means saved but unfinished.
const SAVED_ON_TIMEOUT = { savedOnTimeout: true }

const KIND_BY_CODE = {
  not_synced: NOT_SYNCED,
  removal_incomplete: REMOVAL_INCOMPLETE,
  drive_not_found: DRIVE_NOT_FOUND,
  not_awaiting_review: NOT_AWAITING_REVIEW,
  in_review: IN_REVIEW,
}

const request = (dispatch, path, method, body) =>
  fetchingAPI(`${BASE()}${path}`, method, dispatch, body, undefined, SILENT, USE_OWN_ORG)

const kindOf = (rejection, { sentCursor, savedOnTimeout }) => {
  const code = rejection?.detail?.code
  if (has(KIND_BY_CODE, code)) {
    return KIND_BY_CODE[code]
  }
  if (rejection?.status === NOT_FOUND_STATUS) {
    return NOT_FOUND
  }
  if (savedOnTimeout && rejection?.status === GATEWAY_TIMEOUT) {
    return REMOVAL_INCOMPLETE
  }

  return sentCursor && rejection?.status === 422 ? INVALID_CURSOR : REQUEST_FAILED
}

// A 409 rejects with its parsed body, a 422 with the string detail (read by
// apiErrorMessage), a 404 with the body under .data. A cursor only goes stale on a 422.
const errorOf = (rejection, { sentCursor = false, savedOnTimeout = false } = {}) => {
  const notFoundDetail = isString(rejection?.data?.detail) ? rejection.data.detail : null

  return {
    kind: kindOf(rejection, { sentCursor, savedOnTimeout }),
    message: rejection?.detail?.message ?? notFoundDetail ?? apiErrorMessage(rejection, null),
  }
}

export const fetchConnectionDrives =
  ({ connectionId }) =>
  async (dispatch) => {
    try {
      const { drives } = await request(dispatch, `/${connectionId}/drives`, 'GET')
      return { data: drives }
    } catch (rejection) {
      return { error: errorOf(rejection) }
    }
  }

export const fetchConnectionItems =
  ({ connectionId, driveId, parentId, cursor }) =>
  async (dispatch) => {
    const query = queryString.stringify(
      { drive_id: driveId, parent_id: parentId, cursor },
      { skipNull: true }
    )
    try {
      const page = await request(dispatch, `/${connectionId}/items?${query}`, 'GET')
      return { data: page }
    } catch (rejection) {
      return { error: errorOf(rejection, { sentCursor: !isNil(cursor) }) }
    }
  }

// The query text rides in the body, not the URL, so file names stay out of logs.
export const searchConnectionItems =
  ({ connectionId, query, roots }) =>
  async (dispatch) => {
    try {
      const found = await request(
        dispatch,
        `/${connectionId}/items/search`,
        'POST',
        JSON.stringify({ q: query, roots })
      )
      return { data: found }
    } catch (rejection) {
      return { error: errorOf(rejection) }
    }
  }

export const saveBlockedItems =
  ({ connectionId, changes }) =>
  async (dispatch) => {
    try {
      const saved = await request(
        dispatch,
        `/${connectionId}/blocked_items`,
        'PUT',
        JSON.stringify({ changes })
      )
      return { data: saved }
    } catch (rejection) {
      return { error: errorOf(rejection, SAVED_ON_TIMEOUT) }
    }
  }

export const finishDriveRemoval =
  ({ connectionId, driveId }) =>
  async (dispatch) => {
    try {
      const finished = await request(
        dispatch,
        `/${connectionId}/drives/${encodeURIComponent(driveId)}/finish_removal`,
        'POST'
      )
      return { data: finished }
    } catch (rejection) {
      return { error: errorOf(rejection, SAVED_ON_TIMEOUT) }
    }
  }

// Resolves { review_state, label, roots, nested_root_ids }.
export const fetchConnectionReview =
  ({ connectionId }) =>
  async (dispatch) => {
    try {
      const review = await request(dispatch, `/${connectionId}/review`, 'GET')
      return { data: review }
    } catch (rejection) {
      return { error: errorOf(rejection) }
    }
  }

// The review commits in one request and nothing runs after it, so a 504 is a plain failure.
export const submitConnectionReview =
  ({ connectionId, blocked }) =>
  async (dispatch) => {
    try {
      const submitted = await request(
        dispatch,
        `/${connectionId}/review`,
        'POST',
        JSON.stringify({ blocked })
      )
      return { data: submitted }
    } catch (rejection) {
      return { error: errorOf(rejection) }
    }
  }
